import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilteredViewOption } from './ViewSelector';

interface UseViewPersistenceProps {
    source: string;
    organizationId?: string;
    filteredViews: FilteredViewOption[];
    filterMapMemo: Map<string, any>;
    onFiltersApply: (filters: any, columns?: string[]) => void;
    onViewSelect: (view: FilteredViewOption | null) => void;
    isViewModified?: boolean;
    setIsViewModified?: (modified: boolean) => void;
}

interface UseViewPersistenceReturn {
    saveViewToStorage: (viewId: string | null, viewName: string | null, explicitlyCleared?: boolean) => void;
    getViewFromStorage: () => { viewId: string; viewName: string } | null;
    updateURLWithView: (viewId: string | null) => void;
    clearSelectedView: () => void;
    initializeViewFromStorage: () => void;
}

const useViewPersistence = ({
    source,
    organizationId,
    filteredViews,
    filterMapMemo,
    onFiltersApply,
    onViewSelect,
    isViewModified = false,
    setIsViewModified,
}: UseViewPersistenceProps): UseViewPersistenceReturn => {
    const [searchParams, setSearchParams] = useSearchParams();

    const getStorageKey = useCallback(() => {
        // Use the same storage key for DEAL_VIEW and MANDATE_VIEW since they should share the same view state
        // This ensures that columns, filters, and view selections are preserved when switching between them
        if (source === 'MANDATE_VIEW' || source === 'DEAL_VIEW') {
            return `filteredView_DEALS_${organizationId}`;
        }
        return `filteredView_${source}_${organizationId}`;
    }, [source, organizationId]);

    const saveViewToStorage = useCallback(
        (viewId: string | null, viewName: string | null, explicitlyCleared?: boolean) => {
            const key = getStorageKey();
            try {
                if (viewId && viewName) {
                    localStorage.setItem(key, JSON.stringify({ viewId, viewName, explicitlyCleared: false }));
                } else if (explicitlyCleared) {
                    // Store that this view was explicitly cleared
                    localStorage.setItem(key, JSON.stringify({ viewId: null, viewName: null, explicitlyCleared: true }));
                } else {
                    localStorage.removeItem(key);
                }
            } catch (error) {
                console.warn('Failed to save view to localStorage:', error);
            }
        },
        [getStorageKey],
    );

    const getViewFromStorage = useCallback(() => {
        const key = getStorageKey();
        try {
            const stored = localStorage.getItem(key);
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            console.warn('Failed to load view from localStorage:', error);
            return null;
        }
    }, [getStorageKey]);

    const updateURLWithView = useCallback(
        (viewId: string | null) => {
            const newParams = new URLSearchParams(searchParams);
            if (viewId) {
                newParams.set('viewId', viewId);
            } else {
                newParams.delete('viewId');
            }
            setSearchParams(newParams, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    const clearSelectedView = useCallback(() => {
        onViewSelect(null);
        saveViewToStorage(null, null, true); // Mark as explicitly cleared
        updateURLWithView(null);
    }, [onViewSelect, saveViewToStorage, updateURLWithView]);

    const initializeViewFromStorage = useCallback(() => {
        // Don't re-initialize if the view has been modified
        if (isViewModified) {
            return;
        }

        if (filteredViews.length === 0) return;

        const storedView = getViewFromStorage();

        // If the view was explicitly cleared, clear URL params and don't restore anything
        if (storedView?.explicitlyCleared) {
            updateURLWithView(null);
            return;
        }

        const urlViewId = searchParams.get('viewId');
        let viewToSelect: FilteredViewOption | null = null;

        // Priority: URL > localStorage
        if (urlViewId) {
            viewToSelect = filteredViews.find((view) => view.value === urlViewId) || null;
        } else if (storedView?.viewId) {
            viewToSelect = filteredViews.find((view) => view.value === storedView.viewId) || null;
        }

        if (viewToSelect) {
            onViewSelect(viewToSelect);
            const filterData = filterMapMemo.get(viewToSelect.value);
            if (filterData) {
                // Apply both filters and columns
                onFiltersApply(filterData.filters, viewToSelect.columns);
                saveViewToStorage(viewToSelect.value, viewToSelect.label);

                // Update URL if it wasn't already set
                if (!urlViewId) {
                    updateURLWithView(viewToSelect.value);
                }
            }
        }
    }, [
        filteredViews,
        filterMapMemo,
        searchParams.get('viewId'), // Only track viewId, not entire searchParams
        getViewFromStorage,
        onFiltersApply,
        saveViewToStorage,
        updateURLWithView,
        onViewSelect,
        isViewModified,
    ]);

    // Track if we've already initialized to prevent re-initialization
    const hasInitializedRef = useRef(false);

    // Initialize view from URL or localStorage when data is available
    useEffect(() => {
        // Only initialize once when filteredViews are loaded
        if (filteredViews.length > 0 && !hasInitializedRef.current) {
            initializeViewFromStorage();
            hasInitializedRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredViews.length]); // Intentionally not including initializeViewFromStorage to prevent re-initialization

    // Reset initialization flag when source changes
    useEffect(() => {
        // Don't reset initialization when switching between DEAL_VIEW and MANDATE_VIEW
        // as these should share the same view state
        if (source === 'DEAL_VIEW' || source === 'MANDATE_VIEW') {
            return;
        }
        hasInitializedRef.current = false;
    }, [source]);

    // Note: clearSelectedView is now properly exposed via FilteredViewsButton component

    return {
        saveViewToStorage,
        getViewFromStorage,
        updateURLWithView,
        clearSelectedView,
        initializeViewFromStorage,
    };
};

export default useViewPersistence;
